import { css } from "@emotion/react";
import { Button, Card, Chip } from "@mui/material";
import dayjs from "dayjs";
import { NextPage } from "next";
import Link from "next/link";
import { FC, useCallback, useEffect, useState } from "react";
import { match } from "ts-pattern";

import CreateAccountLead from "~/components/common/CreateAccountLead";
import LoadingIndicator from "~/components/common/LoadingIndicator";
import PageHead from "~/components/common/PageHead";
import { useGraphqlClient } from "~/lib/client/hooks/useGraphqlClient";
import { Layout } from "~/lib/client/layout";
import {
  NotificationIndexContentQuery,
  ServiceNotificationKindNameEnum,
} from "~/lib/graphql/client";
import { useAppContext } from "~/pages/_app";

type Notification =
  NotificationIndexContentQuery["serviceNotifications"][number];

const NotificationKindChip: FC<{
  kind: Notification["serviceNotificationKindName"];
}> = ({ kind }) => {
  const color = match(kind)
    .with(ServiceNotificationKindNameEnum.CompleteQuestEvent, () => "#164381")
    .with(ServiceNotificationKindNameEnum.CreateResponseEvent, () => "#a43232")
    .with(ServiceNotificationKindNameEnum.ResponseGainedPoint, () => "#541c75")
    .with(ServiceNotificationKindNameEnum.ReportWeeklyAction, () => "#a43232")
    .with(ServiceNotificationKindNameEnum.QuestTaskRejected, () => "#ffd336")
    .with(ServiceNotificationKindNameEnum.SpecialMessage, () => "#a43232")
    .exhaustive();

  const label = match(kind)
    .with(ServiceNotificationKindNameEnum.QuestTaskRejected, () => "クエスト")
    .with(ServiceNotificationKindNameEnum.CompleteQuestEvent, () => "クエスト")
    .with(
      ServiceNotificationKindNameEnum.CreateResponseEvent,
      () => "データベース"
    )
    .with(ServiceNotificationKindNameEnum.ResponseGainedPoint, () => "ポイント")
    .with(
      ServiceNotificationKindNameEnum.ReportWeeklyAction,
      () => "今週のFUSSY"
    )
    .with(
      ServiceNotificationKindNameEnum.SpecialMessage,
      () => "特別なメッセージ"
    )
    .exhaustive();

  return (
    <Chip
      label={label}
      sx={{
        background: color,
        color: "#eee",
        "& .MuiChip-label": { webkitTextStrokeColor: "black" },
      }}
    />
  );
};

const Page: NextPage = () => {
  const client = useGraphqlClient();
  const { currentUser } = useAppContext();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [fetched, setFetched] = useState(false);
  const markAsRead = useCallback(
    async (notificationId: number) => {
      if (!client) {
        return;
      }

      await client.markAsReadNotification({ id: notificationId });
      setNotifications((notifications) =>
        notifications.map((notification) => {
          if (notification.id == notificationId) {
            return {
              ...notification,
              isRead: true,
            };
          }
          return notification;
        })
      );
    },
    [client]
  );
  const markAsReadAllNotifications = useCallback(async () => {
    if (!client) {
      return;
    }

    await client.markAsReadAllNotifications();
    setNotifications((notifications) =>
      notifications.map((notification) => {
        return {
          ...notification,
          isRead: true,
        };
      })
    );
  }, [client]);

  useEffect(() => {
    if (!client) {
      return;
    }

    (async () => {
      const { serviceNotifications } = await client.notificationIndexContent();
      serviceNotifications.sort(
        (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix()
      );
      setNotifications(serviceNotifications);
      setFetched(true);
    })();

    return () => {
      setFetched(false);
    };
  }, [client]);

  if (currentUser === undefined) {
    return (
      <>
        <PageHead />
        <Layout layoutType={"YTLayout"}>
          <LoadingIndicator />
        </Layout>
      </>
    );
  }

  if (!currentUser) {
    return (
      <>
        <PageHead />
        <Layout layoutType={"YTLayout"}>
          <main
            css={css`
              padding: 1rem;
            `}
          >
            <CreateAccountLead label={"ログインが必要な画面です。"} />
          </main>
        </Layout>
      </>
    );
  }

  return (
    <>
      <PageHead />
      <Layout layoutType={"YTLayout"}>
        <main
          css={css`
            padding: 1rem;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: flex-end;
              margin-bottom: 1rem;
            `}
          >
            <Button
              onClick={markAsReadAllNotifications}
              variant={"outlined"}
              disabled={notifications.every(
                (notification) => notification.isRead
              )}
            >
              全て既読にする
            </Button>
          </div>
          <div
            css={css`
              display: flex;
              flex-flow: column nowrap;
              gap: 1rem;
            `}
          >
            {notifications.map((notification) => (
              <Link
                href={`/notification/${notification.id}`}
                key={notification.id}
              >
                <Card key={notification.id} sx={{ p: 1 }}>
                  <p
                    css={css`
                      font-weight: bold;
                    `}
                  >
                    {notification.title}
                  </p>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      margin-right: 0.5rem;
                    `}
                  >
                    <NotificationKindChip
                      kind={notification.serviceNotificationKindName}
                    />
                    {notification.isRead ? null : (
                      <Button
                        onClick={() => markAsRead(notification.id)}
                        color={"secondary"}
                      >
                        既読にする
                      </Button>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </main>
      </Layout>
    </>
  );
};

export default Page;
